/**
 * Reports (RP-01/02/03) and the project timeline (TL-01).
 */

import { request } from './client.js';
import { pageCall, type RegisterPage } from './paging.js';
import type { Exposure, Receivables } from './commercial.js';

// ---------------------------------------------------------------------------
// Reports (RP-01/02/03) and the timeline (TL-01)
// ---------------------------------------------------------------------------

export interface ReportSummary {
  readonly id: string;
  readonly type: string;
  readonly period_start: string;
  readonly period_end: string;
  readonly status: 'draft' | 'issued' | 'discarded' | string;
  readonly generated_at: string;
  readonly generated_by_name: string | null;
  readonly issue_count: number;
}

/**
 * The gathered week. Every figure comes from the same computations the other
 * sections read — the report can never disagree with the screens.
 */
export interface ReportContent {
  readonly period: { readonly start: string; readonly end: string };
  readonly pulse: {
    readonly overall: 'green' | 'amber' | 'red';
    readonly measured: number;
    readonly signals: readonly {
      readonly key: string;
      readonly status: string;
      readonly drivers: readonly unknown[];
    }[];
  };
  readonly site: {
    readonly diaries_submitted: number;
    readonly working_days: number;
    readonly progress_entries: number;
    readonly tasks_done: number;
  };
  readonly design: {
    readonly revisions_issued: number;
    readonly submittals_decided: number;
    readonly submittals_overdue: number;
  };
  readonly procurement: {
    readonly deliveries: number;
    readonly deliveries_with_damage: number;
  };
  readonly quality: {
    readonly inspections_concluded: number;
    readonly ncrs_raised: number;
    readonly ncrs_closed: number;
    readonly snags_raised: number;
    readonly snags_closed: number;
  };
  readonly milestones: { readonly achieved: number };
  /** ABSENT, never null, for a caller without finance:view (ADR-005). */
  readonly commercial?: {
    readonly totals: {
      readonly allowed: string;
      readonly committed: string;
      readonly spent: string;
      readonly forecast: string;
    } | null;
    readonly exposure: Exposure | null;
    readonly receivables: Receivables | null;
  };
  /** J9: the holes, named — "3 of 5 site days have no diary entry". */
  readonly missing: readonly string[];
  /** The PM's words, the only editable part of a report. */
  readonly narrative: { readonly summary: string; readonly next_week: string };
}

export interface ReportIssueRef {
  readonly id: string;
  readonly version_no: number;
  readonly issued_at: string;
  readonly issued_by_name: string | null;
}

export interface ReportDetail extends ReportSummary {
  readonly content: ReportContent;
}

export interface ReportView extends ReportDetail {
  readonly issues: readonly ReportIssueRef[];
}

export interface ReportIssueDetail {
  readonly version_no: number;
  readonly issued_at: string;
  readonly issued_by_name: string | null;
  readonly content: ReportContent;
}

const reportsPath = (projectId: string): string =>
  `/v1/projects/${encodeURIComponent(projectId)}/reports`;

/**
 * The register, which the server bounds. The total obeys the issued-only rule,
 * so a client is never told a draft exists. See `api/paging.ts`.
 */
export function listReports(
  projectId: string,
  limit?: number,
): Promise<RegisterPage<ReportSummary>> {
  return pageCall<ReportSummary>(reportsPath(projectId), limit);
}

export function generateReport(projectId: string, periodEnd?: string): Promise<ReportDetail> {
  return request<ReportDetail>(reportsPath(projectId), {
    method: 'POST',
    body: JSON.stringify(periodEnd !== undefined ? { period_end: periodEnd } : {}),
  });
}

export function getReport(projectId: string, reportId: string): Promise<ReportView> {
  return request<ReportView>(`${reportsPath(projectId)}/${encodeURIComponent(reportId)}`);
}

export function updateReportNarrative(
  projectId: string,
  reportId: string,
  input: { summary?: string; next_week?: string },
): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`${reportsPath(projectId)}/${encodeURIComponent(reportId)}`, {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

export function issueReport(projectId: string, reportId: string): Promise<{ version_no: number }> {
  return request<{ version_no: number }>(
    `${reportsPath(projectId)}/${encodeURIComponent(reportId)}/issue`,
    { method: 'POST' },
  );
}

export function getReportIssue(projectId: string, issueId: string): Promise<ReportIssueDetail> {
  return request<ReportIssueDetail>(
    `/v1/projects/${encodeURIComponent(projectId)}/report-issues/${encodeURIComponent(issueId)}`,
  );
}

export function discardReport(projectId: string, reportId: string): Promise<{ ok: boolean }> {
  return request<{ ok: boolean }>(`${reportsPath(projectId)}/${encodeURIComponent(reportId)}`, {
    method: 'DELETE',
  });
}

export interface TimelineEvent {
  readonly kind: string;
  readonly occurred_on: string;
  readonly title: string;
  readonly detail: string | null;
  readonly actor_name: string | null;
}

export async function listTimeline(
  projectId: string,
  limit?: number,
): Promise<readonly TimelineEvent[]> {
  const query = limit !== undefined ? `?limit=${encodeURIComponent(String(limit))}` : '';
  const body = await request<{ data: readonly TimelineEvent[] }>(
    `/v1/projects/${encodeURIComponent(projectId)}/timeline${query}`,
  );
  return body.data;
}
